/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Table, Space, Button, Popconfirm } from 'antd';
import { setDomain } from '@store/reducers/domains';
import { usePaginationWrapper } from '@hooks/usePaginationWrapper';
import { getCustomNoData } from '@utils/noDataCustom';
import { MESSAGES } from '@translations/labels';
import SVGIconEdit from '@svg/IconEdit';
import SVGIconEye from '@svg/IconEye';
import SVGIconDelete from '@svg/IconDelete';
import { InfoTooltip } from '@components/commons/tooltip/infoTooltip';
import { idField } from '@constants';
import { AppContext } from 'commons/contexts/AppContext';
import { FilterFilled } from '@ant-design/icons';
import { colors } from '@styles/theme';
import { useDeleteDomain } from './useDeleteDomain';
import { FilterComponent, getFilterValues } from '../../common/filters';

const DomainsTable = ({ domains, versionId }) => {
  const dispatch = useDispatch();
  const labels = { ...MESSAGES.manageBot };
  const { deleteDomain } = useDeleteDomain();
  const { paginationWrapper } = usePaginationWrapper();
  const { domainToFilterIntents, setDomainToFilterIntents } = useContext(AppContext);
  const history = useHistory();

  const handleEdit = domain => e => {
    e.preventDefault();
    dispatch(setDomain(domain[idField]));
  };
  const handleDelete = domain => event => {
    event.preventDefault();
    return deleteDomain(domain.agentId, domain[idField]);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
    setDomainToFilterIntents(null);
  };

  const getDistinctLanguages = domainsList => getFilterValues(domainsList, 'language');

  const columns = [
    {
      title: () => (
        <span className="bold">
          {labels.tableDomainName} <InfoTooltip label="List of main categories" />
        </span>
      ),
      key: 'action',
      fixed: true,
      width: 350,
      render: record => {
        return (
          domains.length > 0 && (
            <>
              <Button size="small" type="ghost" onClick={handleEdit(record)} style={{ border: 'none' }}>
                <Link
                  to={`/manage-the-bot/domains/${record[idField]}/${!versionId ? 'edit' : 'view'}`}
                  className="link-secondary block"
                >
                  {record.name}
                </Link>
              </Button>
            </>
          )
        );
      },
    },
    {
      title: () => <span className="bold ">{labels.tableLanguage}</span>,
      dataIndex: 'language',
      key: 'language',
      fixed: true,
      width: 200,
      filterMultiple: false,
      filterIcon: filtered => <FilterFilled style={{ color: filtered ? colors.primary : undefined }} />,
      onFilter: (value, record) => record.language === value,
      ...FilterComponent(domains, handleReset, getDistinctLanguages),
    },
    {
      title: () => <span className="bold ">{labels.tableIntentsNumber}</span>,
      dataIndex: 'intentAmount',
      key: 'intentAmount',
      align: 'left',
      fixed: true,
      width: 200,
    },
    {
      title: () => <span className="bold ">{labels.tableSeeIntents}</span>,
      key: 'action',
      fixed: true,
      width: 200,
      render: record => {
        return (
          domains.length > 0 && (
            <div
              className="link-secondary block"
              onClick={() => {
                setDomainToFilterIntents(record);
                history.push(`/manage-the-bot/corpus/intents`);
              }}
            >
              {labels.tableSeeIntents}
            </div>
          )
        );
      },
    },
    {
      title: '',
      key: 'action',
      fixed: true,
      width: 100,
      render: record => {
        return domains.length > 0 ? (
          <>
            <Space size="middle">
              <Button size="small" type="ghost" onClick={handleEdit(record)} style={{ border: 'none' }}>
                {!versionId ? (
                  <Link to={`/manage-the-bot/domains/${record[idField]}/edit`}>
                    <SVGIconEdit width="14px" height="14px" />
                  </Link>
                ) : (
                  <Link to={`/manage-the-bot/domains/${record[idField]}/view`}>
                    <SVGIconEye width="14px" height="14px" />
                  </Link>
                )}
              </Button>
              {!versionId && (
                <Popconfirm
                  title="Sure to delete this domain? It will remove related intents too."
                  onConfirm={handleDelete(record)}
                >
                  <Button
                    size="small"
                    type="ghost"
                    icon={<SVGIconDelete width="14px" height="14px" />}
                    style={{ border: 'none' }}
                  />
                </Popconfirm>
              )}
            </Space>
          </>
        ) : null;
      },
    },
  ];

  return (
    <Table
      locale={getCustomNoData()}
      columns={columns}
      dataSource={domains}
      rowKey="_id"
      pagination={paginationWrapper}
    />
  );
};

DomainsTable.propTypes = {
  domains: PropTypes.array,
  versionId: PropTypes.string,
};

export { DomainsTable };
